import { DatePipe, DecimalPipe, Location } from "@angular/common";
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { DateTime } from "luxon";
import { of, throwError } from "rxjs";
import { catchError, flatMap, map } from "rxjs/operators";
import { conformToMask } from "text-mask-core";

import { environment } from "../../../environments/environment";
import { TicketCutoffTypeEnum } from "../../enum/cutoff-type.enum";
import { MenuConstants } from "../../infrastructure/MenuConstants";
import {
  OperationType,
  Portfolio,
  TicketStatus,
  TicketType,
} from "../../model/domain/Entity";
import { Futures } from "../../model/domain/Futures";
import { NamedType } from "../../model/domain/Tipo";
import { AuthService } from "../../services/auth.service";
import { CutoffService } from "../../services/cutoff.service";
import { DialogService } from "../../services/dialog.service";
import { LoaderService } from "../../services/loader.service";
import { SnackbarService } from "../../services/snackbar.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { AnnotationComponent } from "../annotation/annotation.component";
import { Roles } from "../auth/auth.role";
import { TicketCutoffPermission } from "../cutoff/ticket-cutoff-type.model";
import { DadosFundoComponent } from "../dados-fundo/dados-fundo.component";

@Component({
  selector: "app-futuros",
  templateUrl: "./futuros.component.html",
  styleUrls: ["./futuros.component.scss"],
})
export class FuturosComponent implements OnInit, OnDestroy {
  model: Futures = null;

  navigationSubscription;

  canValidate = false;
  isEditing = false;
  TiposCompra: NamedType<number>[];

  history: any[];

  @ViewChild("thisForm", { static: true }) public frm: NgForm;
  @ViewChild("portfolio", { static: true }) fundoComponent: DadosFundoComponent;
  @ViewChild("annotation", { static: true })
  annotationComponent: AnnotationComponent;

  TicketTypeEnum = TicketType;

  dataChanged: EventEmitter<string> = new EventEmitter<string>();
  isNotProduction = environment.name != 'production';

  constructor(
    private service: TicketService,
    public validator: ValidatorService,
    private snackBarService: SnackbarService,
    private dialogService: DialogService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private location: Location,
    private loaderService: LoaderService,
    private cutoffService: CutoffService
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        this.resetForm();
      }
    });
  }

  validators = {
    brokerDocument: (value) => this.validator.validateDocument(value),
    operationTypeId: (value) =>
      [OperationType.Application, OperationType.Redemption].includes(value),
    unitPrice: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.currencyMask, {}),
    percentageDiscount: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.decimalMask, {}),
    tradingDate: (value) => this.model.tradingDate != null && value != "",
  };

  ngOnInit() {
    this.getTicket();
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  resetForm() {
    this.frm.resetForm();
    this.model = new Futures();

    this.fundoComponent.resetForm();
    this.fundoComponent.portfolio = new Portfolio();

    this.annotationComponent.annotation = null;

    this.isEditing = false;
    this.canValidate = false;

    this.model.operationDate = new Date();
    this.dataChanged.emit("");
    this.setEnable(true);
  }

  setEnable(enabled: boolean = false) {
    setTimeout(() => {
      if (enabled) {
        this.fundoComponent.setEnable(true);
        Object.values(this.frm.controls).forEach((c) => {
          c.enable();
        });
      } else if (!this.canEdit()) {
        this.fundoComponent.setEnable(false);
        this.annotationComponent.annotationIsDisabled = true;
        Object.values(this.frm.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  getTicket(idBoleta: number = null) {
    this.model = new Futures();
    this.TiposCompra = this.service.ListStandardOperationTypes();

    const id =
      idBoleta === null ? +this.route.snapshot.paramMap.get("id") : idBoleta;
    this.model.id = id;
    this.model.operationTypeId = OperationType.Application;

    if (id) {
      this.service.Futures.getTicket(id).subscribe(
        (q) => {
          Object.assign(this.model, q);

          this.model.brokerDocument = this.validator.formatDocument(
            this.model.brokerDocument
          );

          this.model.percentageDiscount = this.decimalPipe.transform(
            this.model.percentageDiscount,
            "1.0-8",
            "pt-BR"
          ) as any;

          this.model.unitPrice = this.decimalPipe.transform(
            this.model.unitPrice,
            "1.0-8",
            "pt-BR"
          ) as any;

          this.model.tradingDate = this.datePipe.transform(
            this.model.tradingDate,
            "dd/MM/yyyy"
          ) as any;

          this.fundoComponent.fillComponentFieldsLoad(this.model);

          this.isEditing = true;
          this.canValidate = true;

          this.dataChanged.emit("");

          this.setEnable(false);
        },
        () => this.router.navigate([MenuConstants.Tickets.Futures])
      );
    } else {
      if (!this.model.statusId) {
        this.model.operationDate = new Date();
      }
    }
  }

  submit() {
    this.loaderService.addLoader();

    const editing = this.isEditing;

    this.register(this.frm)
      .pipe(
        catchError((err) => {
          this.loaderService.removeLoader();

          this.snackBarService.open(err, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
          return throwError(err);
        })
      )
      .subscribe(
        (r) => {
          this.loaderService.removeLoader();

          const msg = editing ? "Boleta alterada." : "Boleta cadastrada.";

          this.snackBarService.open(msg, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
        },
        () => {}
      );
  }

  register(f: NgForm, duplicate: boolean = false) {
    const editing = this.isEditing;

    if (!this.canEdit() && !duplicate) {
      return;
    }

    Object.values(f.controls).forEach((c) => {
      c.markAsTouched();
      c.updateValueAndValidity();
    });

    this.canValidate = true;

    const errors = this.getErrors();

    this.fundoComponent.validatePortfolioFields();
    const fundErrors = this.fundoComponent.getErrors();

    if (f.invalid || errors.length > 0 || fundErrors.length > 0) {
      return throwError("Verifique os campos destacados.");
    }

    let data = Object.assign({}, this.model);

    data.operationDate = null;
    data.statusId = null;
    data.id = null;

    data.amount = this.validator.getCurrency(data.amount);
    data.percentageDiscount = this.validator.getCurrency(
      data.percentageDiscount
    );
    data.unitPrice = this.validator.getCurrency(data.unitPrice);
    data.brokerDocument = this.validator.sanitize(data.brokerDocument);

    data = this.fundoComponent.fillPortfolioData(
      data,
      this.fundoComponent.portfolio
    );

    data.annotations = this.annotationComponent.annotation;

    data.tradingDate = new Date(
      data.tradingDate
        .toString()
        .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
    );

    if (duplicate) {
      data.workflowStartDate = null;
      data.workflowEndDate = null;
    }

    const method =
      editing && !duplicate
        ? this.service.Futures.updateTicket(this.model.id, data)
        : this.service.Futures.registerTicket(data);

    return method
      .pipe(
        catchError((err) => {
          return throwError("Erro ao cadastrar a boleta.");
        })
      )
      .pipe(
        map((future: Futures) => {
          if (!editing) {
            this.model.id = future.id;
            this.model.operationDate = future.operationDate;
            this.model.statusId = future.statusId;
            this.model.accountManagerName = future.accountManagerName;
            this.model.accountManagerDocument = future.accountManagerDocument;
            this.model.ticketCutoffTypeId = future.ticketCutoffTypeId;
            this.model.portfolioFundId = future.portfolioFundId;

            this.isEditing = true;

            const url = this.router.createUrlTree([this.model.id], {
              relativeTo: this.route,
            });
            this.location.go(url.toString());
          }

          return this.model;
        })
      );
  }

  confirmForward(): Promise<boolean> {
    if (!this.canEdit()) {
      return Promise.resolve(false);
    }

    var textConfirm = 'A boleta não poderá mais ser editada, deseja continuar?';

    if(this.isNotProduction) {
      textConfirm += '<br />ATENÇÃO: ESTE É UM AMBIENTE DE TESTE. NÃO EXECUTE OPERAÇÕES REAIS.';
    }

    return this.dialogService
      .confirm(
        "Encaminhar Boleta",
        textConfirm
      )
      .toPromise()
      .then((result) => {
        if (!result) {
          return false;
        }

        const operationDate = DateTime.fromJSDate(
          new Date(this.model.operationDate.toString())
        );

        if (operationDate.day < DateTime.local().day) {
          return this.dialogService
            .confirm(
              "Boleta Retroativa",
              "A data de operação da boleta será alterada para a data de hoje.",
              "OK",
              "Cancelar"
            )
            .toPromise();
        }

        return true;
      });
  }

  async forward(): Promise<void> {
    const cutoffPermission = await this.checkPermission();

    if (!cutoffPermission.isAllowed) {
      this.snackBarService.open(
        this.cutoffService.warningMessage +
          this.cutoffService.dateTimeOffsetToTimeString(
            cutoffPermission.timeLimit
          ),
        "OK"
      );
      return;
    }

    this.confirmForward().then((ok) => {
      if (!ok) {
        return;
      }
      this.register(this.frm)
        .pipe(
          catchError((err) => {
            this.snackBarService.open(err, "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });
            return throwError(err);
          })
        )
        .subscribe(
          (futures: Futures) => {
            this.service.Futures.forwardTicket(futures.id).subscribe((res) => {
              this.model.statusDescription = res.statusDescription;

              this.setEnable(false);

              this.snackBarService.open("Boleta encaminhada.", "OK", {
                verticalPosition: "top",
                horizontalPosition: "right",
              });

              this.getTicket(futures.id);
            });
          },
          () => {}
        );
    });
  }

  cancel() {
    return this.dialogService
      .confirm("Cancelar Boleta", "Deseja cancelar esta boleta?")
      .pipe(
        flatMap((ok) => {
          if (ok) {
            return this.service.Futures.cancelDraftTicket(this.model.id);
          }

          return of(null);
        })
      )
      .subscribe((res) => {
        if (res) {
          this.model.statusDescription = res.statusDescription;

          Object.values(this.frm.controls).forEach((c) => {
            c.disable();
          });

          this.snackBarService.open("Boleta cancelada.", "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          this.getTicket(this.model.id);
        }
      });
  }

  approve() {
    this.dialogService
      .confirm("Aprovar Boleta", "Deseja aprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service.approve(this.model.id, "").subscribe(() => {
            this.snackBarService.open("Boleta atualizada com sucesso.", "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });

            this.getTicket(this.model.id);
          });
        }
      });
  }

  reject(): void {
    this.dialogService
      .reject("Reprovar Boleta", "Deseja reprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service
            .reject(this.model.id, result.justification)
            .subscribe(() => {
              this.snackBarService.open("Boleta atualizada com sucesso.", "OK");
              this.getTicket(this.model.id);
            });
        }
      });
  }

  duplicate() {
    this.register(this.frm, true).subscribe((newTicket) => {
      this.snackBarService
        .open("Boleta duplicada.", "Visualizar no Monitor", {
          verticalPosition: "top",
          horizontalPosition: "right",
          duration: 6000,
        })
        .onAction()
        .subscribe((a) => {
          this.router.navigate([
            MenuConstants.Todos,
            { ticketId: TicketType.Futures },
          ]);
        });
    });
  }

  coalesce(value: any) {
    return value || "";
  }

  hasError(prop: string): boolean {
    if (!this.canValidate) {
      return false;
    }

    return !this.validators[prop](this.model[prop]);
  }

  getErrors(): string[] {
    const validationErrors = [];

    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.model[k])) {
        validationErrors.push(k);
      }
    });

    return validationErrors;
  }

  canEdit() {
    return (
      !this.model.statusId &&
      !this.model.statusDescription &&
      this.authService.hasRole(Roles.CreateTicketFundQuota)
    );
  }

  async checkPermission(): Promise<TicketCutoffPermission> {
    let cutoffTypeId = this.model.ticketCutoffTypeId;
    let accountManagerId = this.model.accountManagerId;
    let fundId = this.model.portfolioFundId;

    if (accountManagerId === null) {
      cutoffTypeId = TicketCutoffTypeEnum.Futures;
      accountManagerId = this.authService.authToken.accountManagerId;
      fundId = this.fundoComponent.portfolio.fundId;
    }
    return this.cutoffService.isAvailable(
      cutoffTypeId,
      this.model.id,
      accountManagerId,
      fundId
    );
  }

  canDuplicate() {
    const _operationDate = this.datePipe.transform(
      this.model.operationDate,
      "yyyy-MM-dd"
    );
    const _duplicateTicketDateLimit = this.datePipe.transform(
      new Date(environment.duplicateTicketDateLimit),
      "yyyy-MM-dd"
    );

    return (
      this.model.id &&
      this.authService.hasRole(Roles.CreateTicketFundQuota) &&
      !(_operationDate <= _duplicateTicketDateLimit)
    );
  }

  canChangeStatus() {
    return (
      this.model.statusId &&
      this.authService.hasRoles(this.model.statusRequiredRoles)
    );
  }

  canCancel() {
    return (
      this.model.id &&
      (!this.model.statusId || this.model.statusId === TicketStatus.Draft) &&
      this.authService.hasRole(Roles.CreateTicketFundQuota)
    );
  }
}
